import { ProjectRepresentative, UserProfile } from '../../types';
import { istPlanungsVerantwortlicher } from '../../services/projectRepresentatives';

/**
 * Block „Verantwortlichkeiten" der Projekt-Stammdaten: drei Rollen auf EINER
 * gespeicherten Liste (docs/projekt-kickoff-plan.md §5, Entscheid 12.10.).
 *
 *   - Hauptverantwortlicher: Position 0, Projektbesitzer, Ausführung.
 *   - Vertreter: alle weiteren Einträge ohne Bereich 'planung'.
 *   - Verantwortliche Planungsphase: Einträge ab Position 1 mit
 *     `bereich: 'planung'` — mehrere Personen möglich.
 *
 * Warum keine eigene Liste je Rolle: `metadata.representatives` ist die
 * Quelle für Projektzugriff (RLS liest die id je Eintrag) und für den
 * Kickoff-Trigger, der je Eintrag eine Kalender-Quittung anlegt. Eine zweite
 * Liste bräuchte beides doppelt. Die Oberfläche teilt deshalb nur auf und
 * fügt beim Speichern wieder zusammen.
 *
 * Reine Funktionen ohne DOM — prüfbar mit vitest.
 */

export interface Verantwortlichkeiten {
    /**
     * Position 0 und alle Vertreter in ihrer bisherigen Reihenfolge. Index 0
     * ist der Hauptverantwortliche. Nur diese Liste kennt Pfeile und
     * Besitzerwechsel — eine Planungsperson ist nie Kandidat für Position 0.
     */
    ausfuehrung: ProjectRepresentative[];
    /** Verantwortliche Planungsphase in ihrer bisherigen Reihenfolge. */
    planung: ProjectRepresentative[];
}

/**
 * Teilt die gespeicherte Liste in die beiden Bereiche.
 *
 * Position 0 bleibt IMMER Hauptverantwortlicher, auch wenn sie (Altdaten,
 * Handpflege) einen Bereich 'planung' trägt: der Eintrag ist Projektbesitzer,
 * und ihn in die Planungsliste zu schieben, machte beim nächsten Speichern den
 * ersten Vertreter stillschweigend zum Besitzer.
 */
export const teileVerantwortlichkeiten = (
    reps: ProjectRepresentative[] | null | undefined,
): Verantwortlichkeiten => {
    const ausfuehrung: ProjectRepresentative[] = [];
    const planung: ProjectRepresentative[] = [];
    (reps || []).forEach((rep, index) => {
        if (index > 0 && istPlanungsVerantwortlicher(rep)) planung.push(rep);
        else ausfuehrung.push(rep);
    });
    return { ausfuehrung, planung };
};

/** Entfernt nur `bereich: 'planung'`; alles andere bleibt unverändert. */
const ohnePlanungsBereich = (rep: ProjectRepresentative): ProjectRepresentative => {
    if (rep.bereich !== 'planung') return rep;
    const { bereich: _entfernt, ...rest } = rep;
    return rest;
};

/**
 * Fügt beide Bereiche wieder zu der EINEN Liste zusammen, die
 * `applyRepresentatives` speichert: Position 0, dann die Vertreter, dann die
 * Planungs-Verantwortlichen.
 *
 * Die Planungseinträge stehen bewusst hinten: so verschiebt sich die
 * Nummerierung der Vertreter (Stellvertreter 1, 2 …) nicht, und die
 * Wochentage bleiben an derselben Person. Der Bereich wird ausdrücklich
 * gesetzt — ein Eintrag aus der Planungsliste darf ihn nie verlieren, sonst
 * stünde er nach dem Speichern als normaler Vertreter da.
 *
 * Umgekehrt verliert jeder Eintrag der Ausführungsliste ein `bereich:
 * 'planung'`. Position 0 gilt immer als Ausführung; trüge sie das Feld noch
 * (Altdaten, Übernahme per `promoteRepresentative`, das den Bereich nicht
 * anfasst), kehrte die Person nach dem Herunterschieben per Pfeil unbemerkt
 * als „nur Planung" zurück — und fiele aus Tourenplanung und Begehungen.
 * Ein Bereich 'ausfuehrung' bleibt stehen, er ist hier zutreffend.
 *
 * Ohne Hauptverantwortlichen gibt es keine Planungseinträge: der erste Eintrag
 * der Liste würde sonst Besitzer (`applyRepresentatives` spiegelt Position 0
 * nach `coordinator*`). Diesen Fall verhindert schon die Oberfläche; die
 * Prüfung hier ist das Sicherheitsnetz, falls ein künftiger Aufrufer es nicht
 * tut — nichts geht dabei verloren, die Liste bleibt dann einfach leer.
 */
export const fuegeVerantwortlichkeitenZusammen = ({ ausfuehrung, planung }: Verantwortlichkeiten): ProjectRepresentative[] => {
    if (ausfuehrung.length === 0) return [];
    return [
        ...ausfuehrung.map(ohnePlanungsBereich),
        ...planung.map(rep => ({ ...rep, bereich: 'planung' as const })),
    ];
};

/** Eintrag aus einem Profil — Werte 1:1, auch leere (siehe ProjectMasterData). */
export const eintragAusProfil = (user: UserProfile): ProjectRepresentative => ({
    id: user.id,
    name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
    email: user.email,
    phone: user.phone,
});

/** Hängt eine Person als Vertreter an (hinter die bisherigen Vertreter). */
export const ergaenzeVertreter = (
    reps: ProjectRepresentative[],
    user: UserProfile,
): ProjectRepresentative[] => {
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(reps);
    // Ohne Hauptverantwortlichen wird der erste Vertreter zu Position 0 —
    // genau wie bisher beim Hinzufügen in eine leere Liste.
    return fuegeVerantwortlichkeitenZusammen({ ausfuehrung: [...ausfuehrung, eintragAusProfil(user)], planung });
};

/**
 * Hängt eine Person als Verantwortliche Planungsphase an.
 * Ohne Hauptverantwortlichen bleibt die Liste unverändert (siehe oben).
 */
export const ergaenzePlanung = (
    reps: ProjectRepresentative[],
    user: UserProfile,
): ProjectRepresentative[] => {
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(reps);
    if (ausfuehrung.length === 0) return reps;
    return fuegeVerantwortlichkeitenZusammen({
        ausfuehrung,
        planung: [...planung, { ...eintragAusProfil(user), bereich: 'planung' }],
    });
};

/** Entfernt einen Vertreter (Index in `ausfuehrung`). Position 0 nie. */
export const entferneVertreter = (
    reps: ProjectRepresentative[],
    index: number,
): ProjectRepresentative[] => {
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(reps);
    if (index <= 0 || index >= ausfuehrung.length) return reps;
    return fuegeVerantwortlichkeitenZusammen({ ausfuehrung: ausfuehrung.filter((_, i) => i !== index), planung });
};

/** Entfernt eine Planungsperson (Index in `planung`). */
export const entfernePlanung = (
    reps: ProjectRepresentative[],
    index: number,
): ProjectRepresentative[] => {
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(reps);
    if (index < 0 || index >= planung.length) return reps;
    return fuegeVerantwortlichkeitenZusammen({ ausfuehrung, planung: planung.filter((_, i) => i !== index) });
};

/** Ändert einen Eintrag der Ausführungsliste (z. B. Wochentage eines Vertreters). */
export const aendereVertreter = (
    reps: ProjectRepresentative[],
    index: number,
    patch: Partial<ProjectRepresentative>,
): ProjectRepresentative[] => {
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(reps);
    if (index < 0 || index >= ausfuehrung.length) return reps;
    return fuegeVerantwortlichkeitenZusammen({
        ausfuehrung: ausfuehrung.map((rep, i) => (i === index ? { ...rep, ...patch } : rep)),
        planung,
    });
};

/**
 * Vertreter → Verantwortliche Planungsphase (Index in `ausfuehrung`).
 * Position 0 nie: der Besitzer wechselt nur über die Pfeile. Wochentage
 * bleiben am Eintrag stehen — sie werden für Planungspersonen nicht
 * ausgewertet, kehren beim Zurückwechseln aber unverändert wieder.
 */
export const wechsleZuPlanung = (
    reps: ProjectRepresentative[],
    index: number,
): ProjectRepresentative[] => {
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(reps);
    if (index <= 0 || index >= ausfuehrung.length) return reps;
    return fuegeVerantwortlichkeitenZusammen({
        ausfuehrung: ausfuehrung.filter((_, i) => i !== index),
        planung: [...planung, { ...ausfuehrung[index], bereich: 'planung' }],
    });
};

/**
 * Verantwortliche Planungsphase → Vertreter (Index in `planung`). Der Bereich
 * fällt dabei weg (beim Zusammenfügen), die Person ist danach für beide
 * Bereiche zuständig wie jeder andere Vertreter.
 */
export const wechsleZuVertreter = (
    reps: ProjectRepresentative[],
    index: number,
): ProjectRepresentative[] => {
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(reps);
    if (index < 0 || index >= planung.length) return reps;
    return fuegeVerantwortlichkeitenZusammen({
        ausfuehrung: [...ausfuehrung, planung[index]],
        planung: planung.filter((_, i) => i !== index),
    });
};

/**
 * Kollegen, die noch in KEINER Rolle stehen. Eine Person kann nur eine Rolle
 * haben: `applyRepresentatives` entfernt Mehrfachnennungen, die vordere
 * gewinnt — eine zusätzlich als Planung eingetragene Vertreterin verschwände
 * beim Speichern also wortlos wieder.
 */
export const waehlbareKollegen = (
    users: UserProfile[],
    reps: ProjectRepresentative[],
): UserProfile[] => users.filter(u => !reps.some(rep =>
    (!!rep.id && rep.id === u.id) ||
    (!!rep.email && !!u.email && rep.email.toLowerCase() === u.email.trim().toLowerCase())
));
